import React, { useState, useEffect } from 'react';
import { Button, Form, Alert, Spinner } from '@/components/ui';
import ResponsiveModal from '../../../components/ResponsiveModal';
import { QuantifiedPlan } from '../../../services/areasService';
import { formatDollars } from '../../../utils/formatDollars';

interface QuantifiedPlanModalProps {
  show: boolean;
  onHide: () => void;
  quantifiedPlan: QuantifiedPlan | null;
  serviceAreaId: number;
  onSave: (planData: Partial<QuantifiedPlan>) => Promise<void>;
}

const QuantifiedPlanModal: React.FC<QuantifiedPlanModalProps> = ({
  show,
  onHide,
  quantifiedPlan,
  serviceAreaId,
  onSave
}) => {
  const [formData, setFormData] = useState({
    status: 'Open' as 'Open' | 'Closed',
    isActive: false,
    year: new Date().getFullYear(),
    startDate: '',
    endDate: '',
    annualTarget: 0,
    annualAdjustment: 0
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (quantifiedPlan) {
      const formatDateForInput = (date: string | Date | undefined): string => {
        if (!date) return '';
        try {
          const d = new Date(date);
          return d.toISOString().split('T')[0];
        } catch {
          return '';
        }
      };

      setFormData({
        status: quantifiedPlan.status || 'Open',
        isActive: Boolean(quantifiedPlan.isActive),
        year: quantifiedPlan.year || new Date().getFullYear(),
        startDate: formatDateForInput(quantifiedPlan.startDate),
        endDate: formatDateForInput(quantifiedPlan.endDate),
        annualTarget: quantifiedPlan.annualTarget || 0,
        annualAdjustment: quantifiedPlan.annualAdjustment || 0
      });
    } else {
      // Default values for new plan
      const currentYear = new Date().getFullYear();
      setFormData({
        status: 'Open',
        isActive: false,
        year: currentYear,
        startDate: `${currentYear}-01-01`,
        endDate: `${currentYear}-12-31`,
        annualTarget: 0,
        annualAdjustment: 0
      });
    }
    setError(null);
  }, [quantifiedPlan, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation
    if (!formData.year || Number.isNaN(formData.year) || formData.year <= 0) {
      setError('Please enter a valid contract year (numeric value)');
      setLoading(false);
      return;
    }

    if (!formData.startDate) {
      setError('Start date is required');
      setLoading(false);
      return;
    }

    if (!formData.endDate) {
      setError('End date is required');
      setLoading(false);
      return;
    }

    const startDate = new Date(formData.startDate);
    const endDate = new Date(formData.endDate);

    if (endDate <= startDate) {
      setError('End date must be after start date');
      setLoading(false);
      return;
    }

    if (formData.annualTarget < 0) {
      setError('Scheduled 2 Contract Amount must be 0 or greater');
      setLoading(false);
      return;
    }

    try {
      await onSave({
        status: formData.status,
        isActive: formData.isActive,
        year: formData.year,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        annualTarget: formData.annualTarget,
        annualAdjustment: formData.annualAdjustment,
        serviceAreaId
      });
      onHide();
    } catch (err: any) {
      console.error('Error saving quantified plan:', err);
      setError(err.response?.data?.error || 'Failed to save quantified plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isFormValid = 
    formData.year > 0 &&
    formData.startDate.length > 0 &&
    formData.endDate.length > 0 &&
    formData.annualTarget >= 0;

  const modalFooter = (
    <>
      <Button variant="secondary" onClick={onHide} disabled={loading}>
        Cancel
      </Button>
      <Button 
        variant="primary" 
        type="submit" 
        disabled={!isFormValid || loading}
        onClick={handleSubmit}
      >
        {loading ? (
          <>
            <Spinner animation="border" size="sm" className="tw-mr-2" />
            Saving...
          </>
        ) : (
          <>
            <i className="bi bi-check-lg tw-mr-2"></i>
            {quantifiedPlan ? 'Update' : 'Create'} Quantified Plan
          </>
        )}
      </Button>
    </>
  );

  return (
    <ResponsiveModal
      isOpen={show}
      onClose={onHide}
      title={quantifiedPlan ? 'Edit Quantified Plan' : 'Create New Quantified Plan'}
      footer={modalFooter}
      size="medium"
    >
      {error && (
        <Alert variant="danger">{error}</Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="tw-mb-3">
          <Form.Label>Status *</Form.Label>
          <Form.Select
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
            required
            disabled={loading}
          >
            <option value="Open">Open</option>
            <option value="Closed">Closed</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="tw-mb-3">
          <Form.Check
            type="checkbox"
            id="qp-is-active"
            label="Active QP year (for quantified clones)"
            checked={formData.isActive}
            onChange={(e) => handleInputChange('isActive', e.target.checked)}
            disabled={loading}
          />
        </Form.Group>

        <Form.Group className="tw-mb-3">
          <Form.Label>Contract Year *</Form.Label>
          <Form.Control
            type="number"
            value={formData.year}
            onChange={(e) => handleInputChange('year', parseInt(e.target.value) || 0)}
            placeholder="Enter contract year"
            required
            disabled={loading}
          />
        </Form.Group>

        <Form.Group className="tw-mb-3">
          <Form.Label>Start Date *</Form.Label>
          <Form.Control
            type="date"
            value={formData.startDate}
            onChange={(e) => handleInputChange('startDate', e.target.value)}
            required
            disabled={loading}
          />
        </Form.Group>

        <Form.Group className="tw-mb-3">
          <Form.Label>End Date *</Form.Label>
          <Form.Control
            type="date"
            value={formData.endDate}
            onChange={(e) => handleInputChange('endDate', e.target.value)}
            required
            disabled={loading}
          />
        </Form.Group>

        <Form.Group className="tw-mb-3">
          <Form.Label>Scheduled 2 Contract Amount *</Form.Label>
          <Form.Control
            type="number"
            step="1"
            min="0"
            value={formData.annualTarget}
            onChange={(e) => handleInputChange('annualTarget', parseInt(e.target.value) || 0)}
            placeholder="Enter scheduled 2 contract amount"
            required
            disabled={loading}
          />
          <Form.Text className="tw-text-content-muted">
            The scheduled 2 contract amount for this quantified plan
          </Form.Text>
        </Form.Group>

        <Form.Group className="tw-mb-3">
          <Form.Label>Annual Adjustment</Form.Label>
          <Form.Control
            type="number"
            step="0.01"
            value={formData.annualAdjustment}
            onChange={(e) => handleInputChange('annualAdjustment', parseFloat(e.target.value) || 0)}
            placeholder="Enter adjustment (can be positive or negative)"
            disabled={loading}
          />
          <Form.Text className="tw-text-content-muted">
            Optional adjustment amount (positive or negative)
          </Form.Text>
        </Form.Group>

        <Form.Group className="tw-mb-3">
          <Form.Label>Target</Form.Label>
          <Form.Control
            type="text"
            value={formatDollars(formData.annualTarget + formData.annualAdjustment)}
            readOnly
            className="tw-bg-surface-alt"
            disabled={loading}
          />
          <Form.Text className="tw-text-content-muted">
            Calculated as: Scheduled 2 Contract Amount + Annual Adjustment
          </Form.Text>
        </Form.Group>
      </Form>
    </ResponsiveModal>
  );
};

export default QuantifiedPlanModal;

